import { resolveApiOrigin } from '../../api/api-origin.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDashboard, useMintEmbedGrant } from '../../api/dashboards.js';
import {
  dashboardPdfUrl,
  isKioskFrameMessage,
  parseKioskFiltersMessage,
  downloadPdf,
  pdfFilename,
  requestFrameFilters,
} from './kiosk-pdf-export.js';

/**
 * Consumer view of a published dashboard — no authoring chrome.
 *
 * Used in two places today:
 *   - InsightsPage in embed mode (?embed=1, Canopy portal Insights
 *     module), where the viewer fills the iframe Canopy gives us.
 *   - Any non-author who lands on a dashboard URL in a standalone
 *     Sage tab (no Designer toolbar to begin with).
 *
 * Render strategy: lean on the existing `/reporting/api/kiosk/
 * dashboards/:id/view` endpoint, which already renders a published
 * dashboard standalone (it's the same surface the NOC-wall kiosk
 * URLs point at). The iframe stretches to fill the available area;
 * kiosk dashboards (fixed canvas by design) center themselves
 * within it.
 *
 * CSAGE-396: the credential for that iframe is an EMBED GRANT, not a
 * share link. The distinction matters twice over:
 *
 *   - Permission. Minting a share link is an administrative act (it
 *     grants unauthenticated access to whoever holds the string), so it
 *     requires DashboardAdmin. This view used it anyway, which meant a
 *     portal user could only OPEN a dashboard if their Canopy role
 *     carried INSIGHTS_DELETE — the only route to DashboardAdmin. Every
 *     read-only Insights user got a 403 and, thanks to the missing
 *     onError below, an eternal "Preparing viewer…".
 *   - Lifetime. It called the share mint with no expiry, so every
 *     dashboard open created a NON-EXPIRING token. Embed grants expire
 *     server-side (~30 min) and never appear in the share-link list.
 *
 * Why a URL credential at all: an iframe cannot send an Authorization
 * header, and fetching the HTML to inject via srcdoc/blob would put
 * author-controlled inline script (unavoidable — CSAGE-309) in the SPA's
 * own origin, where it could reach the user's session. The cross-origin
 * iframe is the isolation boundary, so the grant rides in the URL.
 */
interface Props {
  readonly dashboardId: string;
  /**
   * Hands the host a "download this view as PDF" action once the viewer has a
   * grant, and null while it has none. The host puts it on its header (beside
   * Refresh); the kiosk page itself shows no toolbar in viewer mode.
   */
  readonly onPdfExportChange?: ((exportPdf: (() => void) | null) => void) | undefined;
  /**
   * CSAGE-586: show a standard board as this customer sees it — their settings
   * and their data scope — for an internal admin browsing from the org root.
   */
  readonly asCustomer?: { readonly customer: string; readonly scopePath: string } | undefined;
}

/** Re-grant this far before expiry so the iframe never renders a dead token. */
const REGRANT_MARGIN_MS = 60_000;

function grantErrorMessage(err: unknown): string {
  const status = (err as { status?: number } | null)?.status;
  if (status === 403) {
    return "You don't have permission to view this dashboard. Ask an administrator for Insights access.";
  }
  if (status === 401) {
    return 'Your session has expired. Please sign in again.';
  }
  if (status === 404) {
    return 'This dashboard is no longer available.';
  }
  return 'Could not open this dashboard. Please try again.';
}

export function DashboardViewer({
  dashboardId,
  onPdfExportChange,
  asCustomer,
}: Props): JSX.Element {
  const { data, isLoading, error } = useDashboard(dashboardId);
  const mintGrant = useMintEmbedGrant(dashboardId, asCustomer?.scopePath);
  const [token, setToken] = useState<string | null>(null);
  const [grantError, setGrantError] = useState<string | null>(null);
  // CSAGE-461: the iframe (kiosk page + its data queries) is the slow part of
  // opening a dashboard; show a spinner over it until it fires onLoad so the
  // click doesn't land on a blank frame. Reset whenever the source changes.
  const [frameLoaded, setFrameLoaded] = useState(false);
  // Held in a ref so the re-grant timer can be cleared across renders and
  // on unmount without the timer id participating in render state.
  const regrantTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // `mutate` is not referentially stable across renders; pinning it in a
  // ref keeps `requestGrant` stable so the effect below runs once per
  // dashboard rather than on every render.
  const mutateRef = useRef(mintGrant.mutate);
  mutateRef.current = mintGrant.mutate;

  const requestGrant = useCallback((): void => {
    mutateRef.current(undefined, {
      onSuccess: (res) => {
        setGrantError(null);
        setToken(res.token);
        // Embed grants expire server-side. Renew slightly early so a
        // long-lived board (kiosk chrome auto-refreshes on its own timer)
        // never reloads the iframe against an expired grant.
        const ttl = new Date(res.expiresAt).getTime() - Date.now() - REGRANT_MARGIN_MS;
        if (regrantTimer.current) clearTimeout(regrantTimer.current);
        if (Number.isFinite(ttl) && ttl > 0) {
          regrantTimer.current = setTimeout(() => requestGrant(), ttl);
        }
      },
      // CSAGE-396: previously absent. Without it every failure — 403 from
      // the admin-only mint, 401 on an expired session, even the earlier
      // non-uuid portal-user INSERT crash — left `token` null and rendered
      // "Preparing viewer…" forever, indistinguishable from a slow load.
      onError: (err: unknown) => {
        setToken(null);
        setGrantError(grantErrorMessage(err));
      },
    });
  }, []);

  useEffect(() => {
    setToken(null);
    setGrantError(null);
    if (regrantTimer.current) clearTimeout(regrantTimer.current);
    if (!dashboardId) return;
    requestGrant();
    return () => {
      if (regrantTimer.current) clearTimeout(regrantTimer.current);
    };
  }, [dashboardId, requestGrant]);

  const viewUrl = useMemo(() => {
    if (!token) return null;
    // Anchor on the API origin when set so split-host deploys hit
    // the api host directly; a relative URL would resolve against the
    // SPA host (nginx index.html fallback) and the iframe would render
    // the SPA instead of the dashboard. Matches the same trick used
    // in AiDashboardDesigner.previewUrl.
    const apiOrigin = resolveApiOrigin().replace(/\/+$/u, '');
    // viewer=1: this frame already shows the dashboard name and owns reloads,
    // so the kiosk page drops its own title and its meta-refresh (a reload
    // would also wipe the viewer's filter selections).
    return `${apiOrigin}/reporting/api/kiosk/dashboards/${dashboardId}/view?token=${encodeURIComponent(token)}&viewer=1`;
  }, [dashboardId, token]);

  // PDF export of what the viewer sees: the kiosk frame reports its filter
  // params on every load (each Apply is a reload), and answers on request.
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const lastFilters = useRef<string | null>(null);
  const frameOrigin = useMemo(
    () => (viewUrl ? new URL(viewUrl, window.location.href).origin : null),
    [viewUrl],
  );
  useEffect(() => {
    lastFilters.current = null;
    if (!frameOrigin) return;
    const onMessage = (event: MessageEvent): void => {
      if (!isKioskFrameMessage(event, frameRef.current, frameOrigin)) return;
      const query = parseKioskFiltersMessage(event.data);
      if (query !== null) lastFilters.current = query;
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [frameOrigin]);

  const [pdfState, setPdfState] = useState<'idle' | 'busy' | 'error'>('idle');
  const onPdfExportChangeRef = useRef(onPdfExportChange);
  onPdfExportChangeRef.current = onPdfExportChange;
  // Only a published board can be exported (export.pdf 404s otherwise), and
  // only once the frame has a grant.
  const exportable = !!token && !!frameOrigin && !!data?.currentVersion && !grantError;
  const dashboardName = data?.name ?? 'dashboard';
  useEffect(() => {
    const report = onPdfExportChangeRef.current;
    if (!report) return;
    if (!exportable || !token || !frameOrigin) {
      report(null);
      return;
    }
    const apiOrigin = resolveApiOrigin().replace(/\/+$/u, '');
    report(() => {
      setPdfState('busy');
      // Ask the frame for its live filters; if it doesn't answer within ~1s,
      // use the last ones it reported, else export unfiltered — never nothing.
      void requestFrameFilters(frameRef.current, frameOrigin)
        .then((live) =>
          downloadPdf(
            dashboardPdfUrl(apiOrigin, dashboardId, token, live ?? lastFilters.current ?? ''),
            pdfFilename(dashboardName),
          ),
        )
        .then(
          () => setPdfState('idle'),
          () => setPdfState('error'),
        );
    });
    return () => report(null);
  }, [exportable, dashboardId, token, frameOrigin, dashboardName]);

  // Re-arm the spinner whenever the iframe source changes (switching
  // dashboards, or a re-grant swaps the token) so the next load shows it.
  useEffect(() => {
    setFrameLoaded(false);
  }, [viewUrl]);

  if (isLoading)
    return (
      <div className="text-dim" style={{ padding: 'var(--space-4)' }}>
        Loading dashboard…
      </div>
    );
  if (error || !data) {
    return (
      <div className="alert alert--danger" style={{ margin: 'var(--space-4)' }}>
        Dashboard unavailable.
      </div>
    );
  }
  if (!data.currentVersion) {
    return (
      <div className="text-dim" style={{ padding: 'var(--space-4)' }}>
        No published version yet.
      </div>
    );
  }
  if (grantError) {
    return (
      <div className="alert alert--danger" style={{ margin: 'var(--space-4)' }}>
        {grantError}
      </div>
    );
  }
  if (!viewUrl) {
    return (
      <div className="text-dim" style={{ padding: 'var(--space-4)' }}>
        Preparing viewer…
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: '70vh' }}>
      {asCustomer && (
        <div
          className="alert alert--info"
          role="note"
          style={{ margin: 'var(--space-2) var(--space-4)' }}
        >
          Viewing as <strong>{asCustomer.customer}</strong>: this standard board with their settings
          and data. Change it from the standard dashboards folder.
        </div>
      )}
      {pdfState === 'busy' && (
        <div
          className="text-dim text-sm"
          role="status"
          style={{ padding: 'var(--space-2) var(--space-4)' }}
        >
          Preparing PDF…
        </div>
      )}
      {pdfState === 'error' && (
        <div
          className="alert alert--danger"
          role="alert"
          style={{
            margin: 'var(--space-2) var(--space-4)',
            display: 'flex',
            gap: 'var(--space-3)',
          }}
        >
          <span>Couldn&apos;t export the PDF. Try again in a moment.</span>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setPdfState('idle')}
          >
            Dismiss
          </button>
        </div>
      )}
      {!frameLoaded && (
        <div className="viewer-loading-overlay" role="status" aria-live="polite">
          <div className="viewer-spinner" aria-hidden="true" />
          <div>Loading dashboard…</div>
        </div>
      )}
      <iframe
        ref={frameRef}
        key={viewUrl}
        title={data.name}
        src={viewUrl}
        className="dashboard-viewer-frame"
        onLoad={() => setFrameLoaded(true)}
        style={{
          // Fill the column the viewer is dropped into. The parent
          // (InsightsPage <section>) provides the height; we just
          // expand to it. No border — the dashboard's own background
          // owns the visual frame.
          display: 'block',
          width: '100%',
          height: '100%',
          minHeight: '70vh',
          border: 'none',
          background: 'transparent',
        }}
      />
    </div>
  );
}
